import InstallCertificates from '@/pages/install_certificates/InstallCertificates.vue';
import { logout } from '@/restapi/user/user';
import { showError } from '@dsee/components/popups/toast.js';
import VueRouter from 'vue-router';
import Page403 from './pages/403/403.vue';
import Page404 from './pages/404/404.vue';
import TechWorks from './pages/tech_works/TechWorks.vue';
import BadBrowser from '@/pages/bad_browser/desktop/BadBrowser.vue';
import Login from './pages/user/login/Login.vue';
import Restore from './pages/user/restore/Restore.vue';
import RestoreAwait from './pages/user/restore/RestoreAwait.vue';
import RestoreConfirm from './pages/user/restore/RestoreConfirm.vue';
import Signup from './pages/user/signup/Signup.vue';
import SignupAwait from './pages/user/signup/SignupAwait.vue';
import SignupConfirm from './pages/user/signup/SignupConfirm.vue';

import { getActual, hasRights } from './common/session.js';
import { isBrowserSupport } from './common/browser.support.js';
import { badBrowserLocalGet } from '@/pages/bad_browser/common/config.js';
import { isMobile, setDocumentMeta } from '@/common/util/util.js';

const commonRoutes = [
    {
        path: '/user/login',
        name: 'login',
        component: Login,
        meta: {
            layout: 'Entrance',
            noAuth: true,
            title: 'Авторизация',
        },
    },
    {
        path: '/user/signup',
        name: 'signup',
        component: Signup,
        meta: {
            layout: 'Entrance',
            noAuth: true,
            noMetric: true,
            title: 'Регистрация',
        },
    },
    {
        path: '/user/signup/await',
        name: 'signup-await',
        component: SignupAwait,
        meta: {
            layout: 'Entrance',
            noAuth: true,
            noMetric: true,
            title: 'Подтверждение регистрации',
        },
    },
    {
        path: '/user/signup/confirm',
        name: 'signup-confirm',
        component: SignupConfirm,
        meta: {
            layout: 'Entrance',
            noAuth: true,
            noMetric: true,
            title: 'Подтверждение регистрации',
        },
    },
    {
        path: '/user/restore',
        name: 'restore',
        component: Restore,
        meta: {
            layout: 'Entrance',
            noAuth: true,
            noMetric: true,
            title: 'Восстановление пароля',
        },
    },
    {
        path: '/user/restore/await',
        name: 'restore-await',
        component: RestoreAwait,
        meta: {
            layout: 'Entrance',
            noAuth: true,
            noMetric: true,
            title: 'Восстановление пароля',
        },
    },
    {
        path: '/user/restore/confirm',
        name: 'restore-confirm',
        component: RestoreConfirm,
        meta: {
            layout: 'Entrance',
            noAuth: true,
            noMetric: true,
            title: 'Восстановление пароля',
        },
    },
    {
        path: '/403',
        name: '403',
        component: Page403,
        meta: {
            noMetric: true,
            title: '403',
        },
    },
    {
        path: '*',
        name: '404',
        alias: '/404',
        component: Page404,
        meta: {
            noMetric: true,
            title: '404',
        },
    },
    {
        path: '/tech-works',
        name: 'tech-works',
        component: TechWorks,
        meta: {
            layout: 'Nothing',
            noAuth: true,
            noMetric: true,
            title: 'Технические работы',
        },
    },
    {
        path: '/bad-browser',
        name: 'bad-browser',
        component: BadBrowser,
        meta: {
            layout: 'Nothing',
            noAuth: true,
            title: 'Браузер не поддерживается',
        },
    },
    {
        path: '/install-certificates',
        name: 'install-certificates',
        component: InstallCertificates,
        meta: {
            layout: 'Nothing',
            noAuth: true,
            title: 'Установка сертификатов',
        },
    },
];

export function createRouter(siteRoutes) {
    const routes = [
        ...siteRoutes,
        ...commonRoutes,
    ];

    for (const it of routes) {
        let meta = it.meta;

        if (!meta)
            meta = it.meta = {};

        if (!meta.layout)
            meta.layout = 'Default';
    }

    const router = new VueRouter({
        mode: 'history',
        routes,
        scrollBehavior: (to, from, savedPosition) => {
            const include = [ 'home' ];
            if (include.includes(to.name))
                return savedPosition;

            return { x: 0, y: 0 };
        },
    });

    router.beforeEach((to, from, next) => {
        to.meta.redirectFrom = from;
        const redirect = to.query?.redirect || '/';

        if (to.name === 'bad-browser') {
            if (isBrowserSupport()) {
                next(redirect);
            } else {
                next();
            }
            return;
        }

        if (!isBrowserSupport() && !badBrowserLocalGet() && !isMobile()) {
            next({
                name: 'bad-browser',
                query: { redirect: to.fullPath },
            });
            return;
        }

        getActual().then((user) => {
            if (to.name === 'login') {
                if (user.id && !user.underMaintenance) {
                    next(redirect);
                } else if (user.id && user.underMaintenance) {
                    next({
                        name: 'tech-works',
                        query: { redirect },
                    });
                } else {
                    next();
                }
                return;
            }

            if (to.name === 'tech-works') {
                if (!user.underMaintenance) {
                    next(redirect);
                } else {
                    next();
                }
                return;
            }

            // сайт на тех обслуживании
            if (user.underMaintenance) {
                next({
                    name: 'tech-works',
                    query: { redirect: to.fullPath },
                });
                return;
            }

            // авторизация не требуется
            if (to.meta.noAuth) {
                next();
                return;
            }

            // пользователь не авторизован
            if (!user.id) {
                next({
                    name: 'login',
                    query: { redirect: to.fullPath },
                });
                return;
            }

            if (user.status !== 'ACTIVE') {
                logout();
                next({
                    name: 'login',
                    query: { redirect: to.fullPath },
                });
                showError('Ваша учётная запись недействительна.');
                return;
            }


            let hasAccess = true;

            if (Array.isArray(to.meta.rights)) {
                hasAccess = hasRights(to.meta.rights);
            } else if (typeof (to.meta.rights) === 'function') {
                hasAccess = to.meta.rights();
            }

            // у пользователя не хватает прав на доступ
            if (!hasAccess) {
                console.warn('user has no rights ' + to.meta.rights);
                next({
                    name: '403',
                    query: { path: to.fullPath },
                });

                showError('У вас недостаточно прав для доступа к данной странице');
                return;
            }

            next();
        });
    });

    router.afterEach((to, from) => {
        setDocumentMeta(to.meta.title);
    });

    //  после создания приложения
    router.onReady(() => {
        currentRouter = router.app.$router;
    });

    return router;
}


/** объект текущего роутера приложения.
 * @type {VueRouter}
 */
export let currentRouter = null;
